import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Job } from '@/types';

interface JobMetadataCardProps {
  job: Job;
  submitterName?: string | null;
}

function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getFileType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toUpperCase() ?? 'UNKNOWN';
  if (ext === 'PDF') return 'PDF';
  if (ext === 'DOCX' || ext === 'DOC') return 'DOCX';
  return ext;
}

export function JobMetadataCard({ job, submitterName }: JobMetadataCardProps) {
  const fileType = getFileType(job.file_name);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Job Details</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
              File
            </dt>
            <dd className="font-medium truncate" title={job.file_name}>
              {job.file_name}
            </dd>
          </div>

          <div className="flex gap-6">
            <div>
              <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
                Type
              </dt>
              <dd>
                <Badge variant="outline" className="text-xs">
                  {fileType}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
                Size
              </dt>
              <dd className="text-foreground">{formatFileSize(job.file_size_bytes)}</dd>
            </div>
          </div>

          <div>
            <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
              Submitted
            </dt>
            <dd>{formatDateTime(job.created_at)}</dd>
          </div>

          {submitterName && (
            <div>
              <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
                Submitted by
              </dt>
              <dd>{submitterName}</dd>
            </div>
          )}

          <div>
            <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
              Job ID
            </dt>
            <dd className="font-mono text-xs text-muted-foreground truncate" title={job.id}>
              {job.id}
            </dd>
          </div>

          {job.completed_at && (
            <div>
              <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
                Completed
              </dt>
              <dd>{formatDateTime(job.completed_at)}</dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}
